import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import axios from 'axios';

import { axiosPublic } from 'app/utils';

import type { AcceptInviteRequest, AcceptInviteResponse } from '../types';
import { INVITE_QUERY_KEY } from './useInvite';

// Unknown, already accepted, expired: the invite is no longer in the state the page last saw.
const INVITE_STATE_CHANGED_STATUSES = [404, 409, 410];

async function acceptInvite(
  inviteId: string,
  request: AcceptInviteRequest
): Promise<AcceptInviteResponse> {
  const response = await axiosPublic.post<AcceptInviteResponse>(
    `/v3/invites/${encodeURIComponent(inviteId)}/accept`,
    request
  );

  return response.data;
}

function inviteStateChanged(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    error.response !== undefined &&
    INVITE_STATE_CHANGED_STATUSES.includes(error.response.status)
  );
}

export default function useAcceptInvite(
  inviteId: string
): UseMutationResult<AcceptInviteResponse, unknown, AcceptInviteRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: AcceptInviteRequest) => acceptInvite(inviteId, request),
    onError: (error) => {
      if (!inviteStateChanged(error)) return;

      queryClient.invalidateQueries({ queryKey: [INVITE_QUERY_KEY, inviteId] });
    },
    meta: {
      successMessage: 'Your account is ready. Please log in.',
      errorMessage: 'Unable to set up your account. Please try again.',
    },
  });
}
